import { useEffect, useMemo, useRef, useState } from 'react';
import type { OutputOf } from '@going-once/client';
import { cssVar } from '@going-once/tokens';
import { api } from '../lib/api';
import { describeError } from '../lib/useAsync';
import { isSignedIn, signIn } from '../lib/auth';
import { Box, Card, Pressable, Sheet, Stack, Text } from '../ui/primitives';
import { ErrorNote, Loading } from '../ui/state';
import { formatCents } from './Feed';

/**
 * The three pieces below — the countdown, the status banner and the max-bid sheet — are
 * **exported because the live panel is this screen's anatomy re-skinned**
 * (design/auction-surfaces.md S-A4). Shared components rather than a second
 * implementation: the banner's wording, the increment arithmetic and the server-clock
 * anchoring are the parts of this screen that took the longest to get right, and a live
 * show is exactly where a divergent copy would be noticed last.
 */
/**
 * Moved to `@going-once/client` when `apps/mobile` needed the same rules, and re-exported
 * here so the screens that already import them from this file keep working.
 */
export {
  type AuctionView,
  endsAtLabel,
  formatRemaining,
  nextBidAction,
  type Position,
  sellerLabel,
  timeAgo,
} from '@going-once/client';
import {
  type AuctionView,
  bidPicks,
  bidStatus,
  endsAtLabel,
  formatRemaining,
  nextBidAction,
  placeBidWithRetry,
  type Position,
  remainingMs,
  sellerLabel,
  timeAgo,
} from '@going-once/client';
type Bid = OutputOf<'auctions.bid.history'>['bids'][number];
type Seller = OutputOf<'sellers.profile.get'>['seller'];

/**
 * S-A1, the timed-auction detail screen (design/auction-surfaces.md, frame 10), built
 * against the artboard in `docs/design/canvas/Main.dc.html`.
 *
 * **The status banner is the screen.** Everything else — price, countdown, history — is
 * information a buyer could piece together. The banner is the only thing that states the
 * proxy position in a sentence they can act on: their max, the step, and what happens
 * when it breaks. It carries two channels, color *and* a glyph, because the brief
 * requires a colourblind buyer to read leading-versus-outbid without the label.
 *
 * **Two clocks, and neither of them is this browser's.** The countdown runs locally so it
 * ticks smoothly, but it is anchored to `serverNowMs` from the response: a client whose
 * clock is two minutes fast would otherwise show an auction as over while the server is
 * still taking bids, which is the most confusing possible failure on this screen.
 *
 * **What the artboard shows and we do not.** No watch button (no watchlist capability)
 * and no "96 watching" (no presence). Both are left out rather than faked — a board
 * demo is exactly where a number nobody computes gets asked about.
 */
export function Auction({ auctionId, onBack }: { auctionId: string; onBack: () => void }) {
  const [auction, setAuction] = useState<AuctionView>();
  const [position, setPosition] = useState<Position>();
  const [bids, setBids] = useState<Bid[]>([]);
  const [seller, setSeller] = useState<Seller>();
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [photo, setPhoto] = useState(0);
  /** serverNow − Date.now() at the last fetch: everything time-related is drawn through this. */
  const skew = useRef(0);
  /** Set once the auction is terminal, which is when polling has nothing left to learn. */
  const finished = useRef(false);

  const signedIn = isSignedIn();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [{ auction: fresh }, { bids: book }] = await Promise.all([
          api().query('auctions.auction.get', { auctionId }),
          api().query('auctions.bid.history', { auctionId, limit: 12 }),
        ]);
        if (cancelled) return;
        skew.current = fresh.serverNowMs - Date.now();
        finished.current = fresh.status !== 'active';
        setAuction(fresh);
        setBids(book);
        setError(undefined);
        if (signedIn) {
          const mine = await api().query('auctions.bid.position', { auctionId });
          if (!cancelled) setPosition(mine.position);
        }
      } catch (caught) {
        if (!cancelled) setError(describeError(caught));
      }
    }

    void load();
    // Polling, openly: the per-auction realtime channel is Lane E's (D13 addendum), and
    // until it exists a three-second poll is the honest stand-in rather than a websocket
    // pretending to be one. It is also what closes an expired auction, because a read is
    // what settles it today. It stops once the auction is over — a closed auction cannot
    // change again, and the read is not free.
    const timer = setInterval(() => {
      if (finished.current) return;
      void load();
    }, 3_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [auctionId, signedIn]);

  /**
   * The tab, and what a pasted link previews as. One line, and it is the difference
   * between a shared auction reading as "Going Once" and reading as the thing somebody
   * is being shown.
   */
  useEffect(() => {
    if (!auction) return;
    document.title = `${auction.title} · Going Once`;
    return () => {
      document.title = 'Going Once';
    };
  }, [auction]);

  // The seller's handle, composed client-side from a public read (domains.md seam 5)
  // rather than denormalized onto the auction: a storefront name changes, and a name
  // captured at launch would be the old one for the life of the auction.
  const sellerRef = auction?.sellerRef;
  useEffect(() => {
    if (!sellerRef) return;
    let cancelled = false;
    api()
      .query('sellers.profile.get', { sellerId: sellerRef })
      .then((result) => {
        if (!cancelled) setSeller(result.seller);
      })
      // A missing storefront name is a line that does not render, never an error page.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sellerRef]);

  if (error && !auction) return <ErrorNote error={error} />;
  if (!auction) return <Loading what="the auction" />;

  const closed = auction.status !== 'active';
  const won = closed && position?.standing === 'won';
  if (won) return <Won auction={auction} position={position} seller={seller} onBack={onBack} />;

  return (
    // One column at the width the artboards were drawn at. A bid screen is a single
    // decision — price, clock, one button — and spreading it across a laptop puts the
    // countdown a head-turn away from the button it belongs to.
    <Stack gap="lg" style={{ maxWidth: cssVar('size.phone'), marginInline: 'auto', width: '100%' }}>
      <Stack direction="row" align="center">
        <Pressable variant="quiet" onPress={onBack}>
          ← Back
        </Pressable>
      </Stack>
      <Hero auction={auction} photo={photo} onPhoto={setPhoto} />

      <Stack gap="xs">
        {/* An item's name is upright; `heading` is the italic section label (frame 1i). */}
        <Text as="h1" step="title">
          {auction.title}
        </Text>
        <Text step="label" tone="fg.muted">
          {[auction.condition, auction.categoryPath, sellerLabel(seller)].filter(Boolean).join(' · ')}
        </Text>
      </Stack>

      <Stack direction="row" gap="md">
        <Stat
          label={closed ? 'Winning bid' : 'Current bid'}
          tone="accent"
          value={formatCents(auction.clearingPriceCents ?? auction.currentPriceCents)}
          meta={
            auction.liveBidCount === 0
              ? 'No bids yet'
              : `${auction.liveBidCount} bid${auction.liveBidCount === 1 ? '' : 's'}`
          }
        />
        <Stat
          label={closed ? 'Ended' : 'Ends in'}
          tone="success"
          value={<Countdown endTimeMs={auction.endTimeMs} skewMs={skew.current} closed={closed} />}
          meta={endsAtLabel(auction.endTimeMs)}
        />
      </Stack>

      <StatusBanner auction={auction} position={position} signedIn={signedIn} />

      <Stack gap="sm">
        <Label>Bid history</Label>
        {bids.length === 0 ? (
          <Text tone="fg.muted">Nobody has bid yet. The first bid takes it at the starting price.</Text>
        ) : (
          <Stack gap="sm">
            {bids.map((bid) => (
              <BidRow key={bid.sequence} bid={bid} skewMs={skew.current} />
            ))}
          </Stack>
        )}
      </Stack>

      {error ? <ErrorNote error={error} /> : null}

      {closed ? (
        <Card>
          <Stack gap="xs" align="start">
            <Text step="label" tone="fg.muted">
              {auction.status === 'closed_won' ? 'SOLD' : 'ENDED'}
            </Text>
            <Text tone="fg.muted">
              {auction.status === 'closed_won'
                ? `Won by ${auction.winnerDisplayName ?? 'another bidder'}.`
                : auction.liveBidCount === 0
                  ? 'Nobody bid on this one.'
                  : 'The bidding did not reach the seller’s minimum.'}
            </Text>
          </Stack>
        </Card>
      ) : (
        <ActionBar>
          {signedIn ? (
            <Pressable variant="primary" size="large" grow onPress={() => setSheetOpen(true)}>
              {nextBidAction(auction, position).label}
            </Pressable>
          ) : (
            <Pressable variant="primary" size="large" grow onPress={() => void signIn()}>
              Sign in to bid
            </Pressable>
          )}
          {auction.binCents !== undefined ? <BuyNow auctionId={auctionId} binCents={auction.binCents} /> : null}
        </ActionBar>
      )}

      {sheetOpen ? (
        <MaxBidSheet
          auction={auction}
          position={position}
          skewMs={skew.current}
          onDismiss={() => setSheetOpen(false)}
          onPlaced={(next, mine) => {
            skew.current = next.serverNowMs - Date.now();
            setAuction(next);
            setPosition(mine);
            setSheetOpen(false);
          }}
        />
      ) : null}
    </Stack>
  );
}

/**
 * The hero: the photo, the badge that says what kind of auction this is, and the dots.
 *
 * The gradient is not decoration — it is what the frame looks like before a photo loads
 * and when a seller uploaded only one, which on this platform is most of them.
 */
function Hero({ auction, photo, onPhoto }: { auction: AuctionView; photo: number; onPhoto: (index: number) => void }) {
  const keys = auction.mediaKeys;
  const current = keys[Math.min(photo, Math.max(0, keys.length - 1))];

  return (
    <Box
      radius="lg"
      style={{
        position: 'relative',
        aspectRatio: '4 / 3', // token-exempt: frame proportion, not a design value
        overflow: 'hidden',
        background: cssVar('gradient.hero'),
      }}
    >
      {current ? (
        <img
          src={api().mediaUrl(current)}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : null}

      {/* A scrim under the furniture. A photo is whatever the seller shot, and a pale one
          takes the badge and the dots with it. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to bottom, ${cssVar('color.overlay.scrim')}, transparent 35%, transparent 65%, ${cssVar('color.overlay.scrim')})`,
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'absolute', top: cssVar('space.md'), left: cssVar('space.md') }}>
        <Pill>{auction.showRef ? 'Live show' : 'Timed auction · not live'}</Pill>
      </div>

      {keys.length > 1 ? (
        <div
          style={{
            position: 'absolute',
            bottom: cssVar('space.md'),
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: cssVar('space.xs'),
          }}
        >
          {keys.map((key, index) => (
            <button
              key={key}
              type="button"
              aria-label={`Photo ${index + 1}`}
              onClick={() => onPhoto(index)}
              style={{
                width: index === photo ? cssVar('space.md') : cssVar('space.xs'),
                height: cssVar('space.xs'),
                padding: 0,
                border: 'none',
                borderRadius: cssVar('radius.full'),
                background: index === photo ? cssVar('color.overlay.fg') : cssVar('color.border.strong'),
                cursor: 'pointer',
                transition: `width ${cssVar('motion.duration.fast')} ${cssVar('motion.easing.standard')}`,
              }}
            />
          ))}
        </div>
      ) : null}
    </Box>
  );
}

/**
 * S-A2, the max-bid sheet (frame 7).
 *
 * **The line that does the work is the one under the stepper**: "Current bid $112 · next
 * bid $115 · $5 increments". It turns a maximum into a prediction — what will be charged
 * now, and what the ceiling means — and the button repeats it, so the immediate
 * consequence is on the thing being pressed rather than only in the fine print.
 *
 * Every number here comes from the server (`nextBidCents`, `incrementCents`). A client
 * that computed increments would be a second answer to a question the engine already
 * answers, and the one that disagrees is always the client (D9).
 */
export function MaxBidSheet({
  auction,
  position,
  skewMs,
  onDismiss,
  onPlaced,
}: {
  auction: AuctionView;
  position: Position | undefined;
  /** The screen's `serverNowMs − Date.now()`, so the retry's deadline is the server's clock. */
  skewMs: number;
  onDismiss: () => void;
  onPlaced: (auction: AuctionView, position: Position | undefined) => void;
}) {
  const { floorCents: floor } = nextBidAction(auction, position);
  const [maxCents, setMaxCents] = useState(floor);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();

  // The ladder shape is the artboard's and lives in `@going-once/client`, so the phone
  // offers the same headroom choices.
  const picks = useMemo(() => bidPicks(floor, auction.incrementCents), [floor, auction.incrementCents]);

  async function place() {
    setBusy(true);
    setError(undefined);
    try {
      // One silent retry on a rate limit, while the sheet still says it is placing the bid.
      const result = await placeBidWithRetry(
        () => api().command('auctions.bid.place', { auctionId: auction.auctionId, maxCents }, {}),
        { endTimeMs: auction.endTimeMs, skewMs },
      );
      onPlaced(result.auction, result.position);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet onDismiss={onDismiss}>
      <Stack gap="md">
        <Stack gap="xs">
          <Text step="display">Set your max bid</Text>
          <Text tone="fg.muted">
            We bid the minimum needed to keep you on top — up to your max. Nobody sees it but you.
          </Text>
        </Stack>

        <Box background="bg.base" radius="md" border padding="md">
          <Stack gap="sm" align="center">
            <Label>Your maximum</Label>
            <Stack direction="row" gap="lg" align="center">
              <Stepper
                label="Lower your maximum"
                glyph="−"
                onPress={() => setMaxCents((value) => Math.max(floor, value - auction.incrementCents))}
              />
              <Text step="display" tone="success">
                {formatCents(maxCents)}
              </Text>
              <Stepper
                label="Raise your maximum"
                glyph="+"
                onPress={() => setMaxCents((value) => value + auction.incrementCents)}
              />
            </Stack>
            <Text step="label" tone="fg.muted">
              Current bid {formatCents(auction.currentPriceCents)} · next bid {formatCents(auction.nextBidCents)} ·{' '}
              {formatCents(auction.incrementCents)} increments
            </Text>
          </Stack>
        </Box>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', // token-exempt: grid track sizing, not a design value
            gap: cssVar('space.sm'),
          }}
        >
          {picks.map((pick) => (
            <Pick key={pick} label={formatCents(pick)} selected={pick === maxCents} onPress={() => setMaxCents(pick)} />
          ))}
        </div>

        {position ? (
          <Text step="label" tone="fg.muted">
            You have {formatCents(position.exposureCents)} committed across your open auctions, of{' '}
            {formatCents(position.exposureCapCents)}.
          </Text>
        ) : null}

        {error ? <ErrorNote error={error} /> : null}

        <Pressable variant="primary" size="large" grow busy={busy} onPress={() => void place()}>
          {position?.standing === 'winning'
            ? `Raise max to ${formatCents(maxCents)}`
            : `Set max, bid ${formatCents(auction.nextBidCents)} now`}
        </Pressable>
        <Pressable variant="quiet" grow onPress={onDismiss}>
          Maybe later
        </Pressable>
      </Stack>
    </Sheet>
  );
}

/**
 * The proxy-bid explainer. Four states, and the wording of each is the product: a buyer
 * who does not understand what their max is doing will not set a real one.
 */
export function StatusBanner({
  auction,
  position,
  signedIn,
}: {
  auction: AuctionView;
  position: Position | undefined;
  signedIn: boolean;
}) {
  // The tone, the label, the glyph and the words are one decision, and it lives in
  // `@going-once/client` so the live panel and the phone cannot tell a bidder something
  // different about the same standing. This component is the rendering only.
  const status = bidStatus(auction, position, signedIn);
  return (
    <Banner tone={status.tone} label={status.label} glyph={status.glyph}>
      <Text tone={status.tone === 'none' ? 'fg.muted' : 'fg.default'}>{status.body}</Text>
    </Banner>
  );
}

/**
 * S-A3, the won state (frame 12): celebration that reads as confirmation.
 *
 * The artboard puts the charged card on the same screen as the confetti. We have no
 * payment yet, so that line is absent rather than invented — but "$120 under your max"
 * is real, and it closes the loop the max-bid sheet opened.
 */
function Won({
  auction,
  position,
  seller,
  onBack,
}: {
  auction: AuctionView;
  position: Position | undefined;
  seller: Seller | undefined;
  onBack: () => void;
}) {
  const price = auction.clearingPriceCents ?? auction.currentPriceCents;
  const under = position?.maxCents !== undefined ? position.maxCents - price : undefined;

  return (
    <Box
      radius="lg"
      padding="xl"
      style={{ background: cssVar('gradient.celebrate'), maxWidth: cssVar('size.phone'), marginInline: 'auto' }}
    >
      <Stack gap="lg" align="center">
        <Stack align="center">
          <Text step="display" tone="success">
            YOU
          </Text>
          <Text step="hero">
            WON<span style={{ color: cssVar('color.accent.default') }}>!</span>
          </Text>
        </Stack>

        {auction.mediaKeys[0] ? (
          <Box
            radius="lg"
            style={{
              width: cssVar('size.thumbnail'),
              height: cssVar('size.thumbnail'),
              overflow: 'hidden',
              background: cssVar('gradient.hero'),
            }}
          >
            <img
              src={api().mediaUrl(auction.mediaKeys[0])}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        ) : null}

        <Stack gap="xs" align="center">
          <Text>{auction.title}</Text>
          <Label>Winning bid</Label>
          <Text step="display" tone="success">
            {formatCents(price)}
          </Text>
          {under !== undefined && under > 0 ? (
            <Text step="label" tone="fg.muted">
              {formatCents(under)} under your max
            </Text>
          ) : null}
        </Stack>

        <Stack gap="sm" align="center">
          <Pressable variant="primary" size="large" grow onPress={onBack}>
            {sellerLabel(seller) ? `Keep bidding · more from ${sellerLabel(seller)}` : 'Keep bidding'}
          </Pressable>
        </Stack>
      </Stack>
    </Box>
  );
}

function BuyNow({ auctionId, binCents }: { auctionId: string; binCents: number }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();
  // One key per mount: a double tap must not buy the item twice, and the server refuses
  // a keyed command without it (the client will not even compile without one).
  const key = useRef(crypto.randomUUID());
  return (
    <Stack gap="xs">
      <Pressable
        variant="secondary"
        size="large"
        busy={busy}
        onPress={() => {
          setBusy(true);
          api()
            .command('auctions.buynow.purchase', { auctionId }, { idempotencyKey: key.current })
            .catch((caught: unknown) => setError(describeError(caught)))
            .finally(() => setBusy(false));
        }}
      >
        Buy now {formatCents(binCents)}
      </Pressable>
      {error ? <ErrorNote error={error} /> : null}
    </Stack>
  );
}

/** One of the two cards under the title: a label, a number, and the line that qualifies it. */
function Stat({
  label,
  tone,
  value,
  meta,
}: {
  label: string;
  tone: 'accent' | 'success';
  value: React.ReactNode;
  meta: string;
}) {
  return (
    <Box background="bg.raised" radius="md" border padding="md" grow>
      <Stack gap="xs">
        <Label>{label}</Label>
        {/* `stat`, not `countdown`: frame 1i sets these at 22px, and the countdown's 32
            overflows two cards sitting side by side at phone width. */}
        <Text step="stat" tone={tone === 'accent' ? 'danger' : 'success'}>
          {value}
        </Text>
        <Text step="label" tone="fg.muted">
          {meta}
        </Text>
      </Stack>
    </Box>
  );
}

/**
 * The status banner. **Two channels, never color alone** — the brief requires a
 * colourblind buyer to read leading-versus-outbid without the label, so the state carries
 * a glyph as well as a tone, and the left edge carries it a third time.
 */
function Banner({
  tone,
  label,
  glyph,
  children,
}: {
  tone: 'leading' | 'outbid' | 'none';
  label: string;
  glyph: string;
  children: React.ReactNode;
}) {
  const ink = tone === 'leading' ? 'color.bid.leading' : tone === 'outbid' ? 'color.bid.outbid' : 'color.fg.muted';
  const fill = tone === 'leading' ? 'success.subtle' : tone === 'outbid' ? 'warning.subtle' : 'bg.sunken';
  return (
    <Box
      background={fill}
      radius="md"
      padding="md"
      style={{ borderLeft: `${cssVar('size.focus.ring')} solid ${cssVar(ink)}` }}
    >
      <Stack gap="xs" align="start">
        <Stack direction="row" gap="xs" align="center">
          <span aria-hidden style={{ color: cssVar(ink) }}>
            {glyph}
          </span>
          <span
            style={{
              color: cssVar(ink),
              fontSize: cssVar('type.mono.size'),
              fontWeight: cssVar('type.mono.weight'),
              letterSpacing: '0.08em', // token-exempt: tracking has no slot in the brief
              textTransform: 'uppercase',
            }}
          >
            {label}
          </span>
        </Stack>
        {children}
      </Stack>
    </Box>
  );
}

/** A bid, as the book shows it: who (masked), what it made the price, and how long ago. */
function BidRow({ bid, skewMs }: { bid: Bid; skewMs: number }) {
  return (
    <Stack direction="row" gap="sm" align="center">
      <Box
        background={bid.you ? 'accent.subtle' : 'bg.sunken'}
        radius="full"
        style={{
          width: cssVar('size.target.min'),
          height: cssVar('size.target.min'),
          flex: 'none', // token-exempt: layout, not a design value
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text step="label" tone={bid.you ? 'fg.default' : 'fg.muted'}>
          {bid.displayName.slice(0, 1).toUpperCase()}
        </Text>
      </Box>
      <Stack gap="xs" grow>
        <Text truncate>{bid.you ? 'you' : bid.displayName}</Text>
      </Stack>
      <Text>{formatCents(bid.priceCents)}</Text>
      <Text step="label" tone="fg.muted">
        {timeAgo(bid.placedAt, skewMs)}
      </Text>
    </Stack>
  );
}

/** The bar the primary action lives in — sticky, because the CTA should never scroll away. */
function ActionBar({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        gap: cssVar('space.sm'),
        paddingTop: cssVar('space.md'),
        paddingBottom: cssVar('space.md'),
        background: cssVar('color.bg.base'),
        borderTop: `1px solid ${cssVar('color.border.default')}`, // token-exempt: hairline width
      }}
    >
      {children}
    </div>
  );
}

/**
 * A quick pick: a ladder off the next bid, selected or not.
 *
 * Its own control rather than a `Pressable` variant, because the selected state the
 * artboard specifies — teal edge, teal ink, the positive tint behind it — is the
 * *leading* color, and a button variant called "selected" that happens to be teal would
 * put that meaning in the design system by accident.
 */
function Pick({ label, selected, onPress }: { label: string; selected: boolean; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onPress}
      style={{
        minHeight: cssVar('size.target.min'),
        borderRadius: cssVar('radius.md'),
        border: `1px solid ${cssVar(selected ? 'color.bid.leading' : 'color.border.default')}`, // token-exempt: hairline width
        background: selected ? cssVar('color.success.subtle') : 'transparent',
        color: cssVar(selected ? 'color.bid.leading' : 'color.fg.muted'),
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.label.size'),
        fontWeight: cssVar('type.heading.weight'),
        fontVariantNumeric: 'tabular-nums',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function Stepper({ label, glyph, onPress }: { label: string; glyph: string; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onPress}
      style={{
        width: cssVar('size.target.min'),
        height: cssVar('size.target.min'),
        borderRadius: cssVar('radius.full'),
        border: `1px solid ${cssVar('color.border.strong')}`, // token-exempt: hairline width
        background: 'transparent',
        color: cssVar('color.fg.default'),
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.heading.size'),
        cursor: 'pointer',
      }}
    >
      {glyph}
    </button>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <Box
      background="meta"
      radius="full"
      style={{ paddingInline: cssVar('space.sm'), paddingBlock: cssVar('space.xs') }}
    >
      <span
        style={{
          color: cssVar('color.fg.inverse'),
          fontSize: cssVar('type.mono.size'),
          fontWeight: cssVar('type.mono.weight'),
          letterSpacing: '0.06em', // token-exempt: tracking has no slot in the brief
          textTransform: 'uppercase',
        }}
      >
        {children}
      </span>
    </Box>
  );
}

/** The tracked caps the artboards set above every number — the `mono` step, uppercased. */
function Label({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        color: cssVar('color.fg.muted'),
        fontSize: cssVar('type.mono.size'),
        fontWeight: cssVar('type.mono.weight'),
        letterSpacing: '0.08em', // token-exempt: tracking has no slot in the brief
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

/**
 * The countdown. Ticks locally against the server's clock, and stops at zero rather than
 * counting into negative time — a closed auction is settled by the next read.
 */
export function Countdown({ endTimeMs, skewMs, closed }: { endTimeMs: number; skewMs: number; closed: boolean }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (closed) return;
    const timer = setInterval(() => tick((value) => value + 1), 500);
    return () => clearInterval(timer);
  }, [closed]);

  if (closed) return <>—</>;
  return <>{formatRemaining(remainingMs(endTimeMs, skewMs))}</>;
}
